import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/use-subscription";
import { TeamLimitUpgradeDialog } from "@/components/subscription/team-limit-upgrade-dialog";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { DialogWithScrollReset as Dialog } from "@/components/ui/dialog-with-scroll-reset";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LoadingButton } from "@/components/ui/loading-button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Users, UserPlus, Search, Key, Check, ArrowRight } from "lucide-react";

// Form schema
const teamFormSchema = z.object({
  name: z.string().min(1, "Team name is required"),
  description: z.string().optional(),
  department: z.string().optional(),
  manager: z.string().optional(),
});

type TeamFormValues = z.infer<typeof teamFormSchema>;

type EditTeamDialogProps = {
  teamId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onManageMembers?: (teamId: number, teamName: string) => void;
};

export default function EditTeamDialog({
  teamId,
  isOpen,
  onClose,
  onManageMembers,
}: EditTeamDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditMode = teamId !== null;
  const { canAddUser, usage, planType } = useSubscription();
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  // Define the app setting type
  type AppSetting = {
    id: number;
    type: string;
    value: string;
    displayOrder: number;
    isActive: boolean;
  };
  
  // We've removed the department query since we're simplifying organizational structure

  // Form setup
  const form = useForm<TeamFormValues>({
    resolver: zodResolver(teamFormSchema),
    defaultValues: {
      name: "",
      description: "",
      department: "",
      manager: "",
    },
  });

  // Fetch team data if in edit mode
  const { data: team, isLoading: isLoadingTeam } = useQuery<{
    id: number;
    name: string;
    description?: string;
    department?: string;
    manager?: string;
    status?: string;
  }>({
    queryKey: [`/api/teams/${teamId}`],
    enabled: isOpen && isEditMode && !!teamId,
  });

  console.log("Edit mode:", isEditMode, "Team ID:", teamId, "Team data:", team);

  // Update form when team data is loaded
  useEffect(() => {
    if (team) {
      form.reset({
        name: team.name || "",
        description: team.description || "",
        department: team.department || "",
        manager: team.manager || "",
      });
    }
  }, [team, form]);

  // Create states for tracking created/updated team
  const [createdTeamId, setCreatedTeamId] = useState<number | null>(null);
  const [createdTeamName, setCreatedTeamName] = useState<string>("");
  const [currentStep, setCurrentStep] = useState<"info" | "members" | "licenses" | "success">("info");
  
  // Reset states when dialog opens or closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep("info");
      
      // Reset selected employees every time dialog opens
      setSelectedEmployees([]);
      setSelectedLicenses([]);
      
      // Only reset ID and name if not in edit mode
      if (!isEditMode) {
        setCreatedTeamId(null);
        setCreatedTeamName("");
      }
    }
  }, [isOpen, isEditMode]);

  // Create team mutation
  const createTeamMutation = useMutation({
    mutationFn: async (values: TeamFormValues) => {
      try {
        console.log("Creating team with values:", values);
        return await apiRequest("/api/teams", { 
          method: "POST", 
          body: values 
        });
      } catch (error) {
        console.error("Error creating team:", error);
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/teams'] });
      
      console.log("Team created successfully:", data);
      
      // Store the created team information
      setCreatedTeamId(data.id);
      setCreatedTeamName(values => values || data.name);
      
      console.log("Setting teamId:", data.id, "and teamName:", data.name);
      
      // Use setTimeout to ensure state is properly updated before changing steps
      setTimeout(() => {
        setCurrentStep("members");
        
        toast({
          title: "Team created",
          description: "The team has been created successfully. Now you can add members."
        });
      }, 100);
    },
    onError: (error: any) => {
      const errorMessage = error?.message || "Failed to create team. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Update team mutation
  const updateTeamMutation = useMutation({
    mutationFn: async (values: TeamFormValues) => {
      if (!teamId) throw new Error("Team ID is required");
      try {
        console.log("Updating team with values:", values);
        return await apiRequest(`/api/teams/${teamId}`, { 
          method: "PUT", 
          body: values 
        });
      } catch (error) {
        console.error("Error updating team:", error);
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/teams'] });
      queryClient.invalidateQueries({ queryKey: [`/api/teams/${teamId}`] });
      
      console.log("Team updated successfully:", data);
      
      // Store the updated team information
      setCreatedTeamId(data.id);
      setCreatedTeamName(values => values || data.name);
      
      console.log("Setting updated teamId:", data.id, "and teamName:", data.name);
      
      // Use setTimeout to ensure state is properly updated before changing steps
      setTimeout(() => {
        setCurrentStep("members");
        
        toast({
          title: "Team updated",
          description: "The team has been updated successfully. Now you can manage members.",
        });
      }, 100);
    },
    onError: (error: any) => {
      const errorMessage = error?.message || "Failed to update team. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Handle form submission
  function onSubmit(values: TeamFormValues) {
    if (isEditMode) {
      updateTeamMutation.mutate(values);
    } else {
      createTeamMutation.mutate(values);
    }
  }

  // Employee selection state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  
  // Fetch all employees for selection
  const { data: allEmployees = [], isLoading: isLoadingEmployees } = useQuery<{
    id: number;
    fullName: string;
    email: string;
    jobTitle?: string;
    // Removed department field as part of org structure simplification
    status?: string;
  }[]>({
    queryKey: ['/api/employees'],
    enabled: currentStep === "members",
  });
  
  // Filter employees based on search query
  const filteredEmployees = allEmployees.filter((employee) => {
    const matchesSearch =
      searchQuery === "" ||
      employee.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (employee.jobTitle && employee.jobTitle.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesSearch;
  });
  
  // Handle employee selection/deselection
  const toggleEmployee = (employeeId: number) => {
    if (selectedEmployees.includes(employeeId)) {
      setSelectedEmployees(selectedEmployees.filter((id) => id !== employeeId));
    } else {
      setSelectedEmployees([...selectedEmployees, employeeId]);
    }
  };
  
  // Add team members mutation
  const addTeamMembersMutation = useMutation({
    mutationFn: async () => {
      if (!createdTeamId) throw new Error("Team ID is required");
      
      try {
        // Send all employee IDs in a single request
        return await apiRequest(`/api/teams/${createdTeamId}/members`, { 
          method: "POST", 
          body: { employeeIds: selectedEmployees.map(id => Number(id)) } 
        });
      } catch (error) {
        console.error("Error adding team members:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/teams'] });
      queryClient.invalidateQueries({ queryKey: ['/api/teams', createdTeamId, 'members'] });
      
      toast({
        title: "Team members added",
        description: `${selectedEmployees.length} team members have been added successfully.`,
      });
      
      // Do not close the dialog here - proceed to step 3 instead
      // This will be handled in the proceedToLicenses function
    },
    onError: (error: any) => {
      console.error("Team member addition error:", error);
      
      toast({
        title: "Error",
        description: `Failed to add team members: ${error.message || "Please try again."}`,
        variant: "destructive",
      });
    },
  });
  
  // License selection state
  const [licenseSearchQuery, setLicenseSearchQuery] = useState("");
  const [selectedLicenses, setSelectedLicenses] = useState<number[]>([]);
  
  // Fetch all available licenses with enhanced error handling
  const { data: availableLicenses = [], isLoading: isLoadingLicenses } = useQuery<{
    id: number;
    name: string;
    application: { name: string; id: number }; // Application data from JOIN
    applicationId: number;
    status: string;
    totalSeats: number;
    assignedSeats: number;
    cost: number;
    currency: string;
    billingCycle: string;
    startDate: string;
    renewalDate: string;
    type?: string; // For backward compatibility with UI
  }[]>({
    queryKey: ['/api/licenses'],
    // Always enable the query to avoid missing data in step 3
    enabled: true
  });
  
  // Filter licenses based on search query
  const filteredLicenses = (availableLicenses as any[]).filter((license) => {
    const matchesSearch =
      licenseSearchQuery === "" ||
      license.name.toLowerCase().includes(licenseSearchQuery.toLowerCase()) ||
      (license.application?.name && 
        license.application.name.toLowerCase().includes(licenseSearchQuery.toLowerCase()));
    
    return matchesSearch;
  });
  
  // Handle license selection/deselection
  const toggleLicense = (licenseId: number) => {
    if (selectedLicenses.includes(licenseId)) {
      setSelectedLicenses(selectedLicenses.filter((id) => id !== licenseId));
    } else {
      setSelectedLicenses([...selectedLicenses, licenseId]);
    }
  };
  
  // Proceed to license assignment step after adding members
  function proceedToLicenses() {
    if (selectedEmployees.length > 0) {
      // Define a custom mutation with onSuccess callback
      const addTeamMembersAndProceed = async () => {
        try {
          console.log("Adding team members with IDs:", selectedEmployees, "to team ID:", createdTeamId);
          await addTeamMembersMutation.mutateAsync();
          setCurrentStep("licenses");
          
          toast({
            title: "Team members added",
            description: `${selectedEmployees.length} team members have been added successfully.`,
          });
        } catch (error: any) {
          console.error("Error in proceedToLicenses:", error);
          
          toast({
            title: "Error",
            description: `Failed to add team members: ${error.message || "Please try again."}`,
            variant: "destructive",
          });
        }
      };
      
      addTeamMembersAndProceed();
    } else {
      setCurrentStep("licenses");
    }
  }
  
  // Assign licenses to team mutation
  const assignLicensesToTeamMutation = useMutation({
    mutationFn: async () => {
      if (!createdTeamId) throw new Error("Team ID is required");
      
      try {
        // Send all license IDs in a single request
        return await apiRequest(`/api/teams/${createdTeamId}/licenses`, { 
          method: "POST", 
          body: { licenseIds: selectedLicenses.map(id => Number(id)) } 
        });

      } catch (error) {
        console.error("Error assigning licenses to team:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/teams'] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      
      toast({
        title: "Licenses assigned",
        description: `${selectedLicenses.length} licenses have been assigned to the team.`,
      });
      
      setCurrentStep("success");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to assign licenses. Please try again.",
        variant: "destructive",
      });
    },
  });
  
  // Handle completing the team setup with members
  function completeTeamSetup() {
    if (selectedLicenses.length > 0) {
      assignLicensesToTeamMutation.mutate();
    } else {
      setCurrentStep("success");
    }
  }
  
  // Handle manage members click (for backward compatibility)
  function handleManageMembers() {
    if (onManageMembers && createdTeamId) {
      onClose();
      onManageMembers(createdTeamId, createdTeamName);
    }
  }

  const isPending = createTeamMutation.isPending || updateTeamMutation.isPending;

  // Update to close dialog directly from buttons rather than just Dialog's onOpenChange
  const handleClose = () => {
    if (createTeamMutation.isPending || updateTeamMutation.isPending) {
      return; // Prevent closing while mutation is in progress
    }
    onClose();
  };

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => !open && handleClose()}
    >
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-hidden flex flex-col">
        {currentStep === "info" && (
          // Step 1: Team Information Form
          <>
            <DialogHeader>
              <DialogTitle>{isEditMode ? "Edit" : "Create"} Team</DialogTitle>
              <DialogDescription>
                {isEditMode 
                  ? "Update the team information below. You can manage team members after updating."
                  : "Step 1: Enter team details. You'll be able to add team members after creating the team."
                }
              </DialogDescription>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Team Name*</FormLabel>
                      <FormControl>
                        <Input placeholder="Engineering Team" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Brief description of the team's purpose"
                          className="resize-none"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="department"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Department</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Engineering, Marketing, Sales, etc."
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="manager"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Manager</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Team manager or lead"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button variant="outline" type="button" onClick={handleClose}>
                    Cancel
                  </Button>
                  <LoadingButton type="submit" isLoading={isPending}>
                    {isEditMode ? "Update" : "Continue to Members"}
                  </LoadingButton>
                </DialogFooter>
              </form>
            </Form>
          </>
        )}
        
        {currentStep === "members" && (
          // Step 2: Member Selection - Embedded employee selection
          <>
            <DialogHeader>
              <DialogTitle>Add Members to {createdTeamName}</DialogTitle>
              <DialogDescription>
                Step 2: Select employees to add to your team. Team members will have access to all licenses assigned to this team.
              </DialogDescription>
            </DialogHeader>
            
            <div className="relative mb-4 mt-2">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search employees by name, email or job title..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            {isLoadingEmployees ? (
              <div className="flex justify-center items-center py-12">
                <LoadingSpinner />
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="text-center p-6 bg-slate-50 rounded-md">
                <p className="text-muted-foreground">No employees found. Try a different search term.</p>
              </div>
            ) : (
              <ScrollArea className="px-1 py-2" style={{height: "300px", maxHeight: "50vh"}}>
                <div className="space-y-2">
                  {filteredEmployees.map((employee) => {
                    const isSelected = selectedEmployees.includes(employee.id);
                    
                    return (
                      <div
                        key={employee.id}
                        className={`flex items-center justify-between p-3 rounded-md ${
                          isSelected ? "bg-primary/5 border border-primary/20" : "hover:bg-muted"
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleEmployee(employee.id)}
                            id={`employee-${employee.id}`}
                          />
                          <div className="flex-1">
                            <label
                              htmlFor={`employee-${employee.id}`}
                              className="text-sm font-medium cursor-pointer"
                            >
                              {employee.fullName}
                            </label>
                            <div className="text-xs text-muted-foreground">
                              {employee.email}
                              {employee.jobTitle && ` • ${employee.jobTitle}`}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
            
            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between items-center">
                <div className="text-sm text-muted-foreground">
                  {selectedEmployees.length > 0 ? (
                    <span>{selectedEmployees.length} employees selected</span>
                  ) : (
                    <span>No employees selected</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={handleClose}
                    disabled={addTeamMembersMutation.isPending}
                  >
                    Skip for Now
                  </Button>
                  <LoadingButton
                    onClick={proceedToLicenses}
                    isLoading={addTeamMembersMutation.isPending}
                    disabled={addTeamMembersMutation.isPending}
                  >
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Continue to Licenses
                  </LoadingButton>
                </div>
              </div>
            </div>
          </>
        )}
        
        {currentStep === "licenses" && (
          // Step 3: License Assignment
          <>
            <DialogHeader>
              <DialogTitle>Assign Licenses to {createdTeamName}</DialogTitle>
              <DialogDescription>
                Step 3: Select licenses to assign to this team. Team members will have access to all assigned licenses.
              </DialogDescription>
            </DialogHeader>
            
            <div className="relative mb-4 mt-2">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search licenses by name or application..."
                className="pl-9"
                value={licenseSearchQuery}
                onChange={(e) => setLicenseSearchQuery(e.target.value)}
              />
            </div>
            
            {isLoadingLicenses ? (
              <div className="flex justify-center items-center py-12">
                <LoadingSpinner />
              </div>
            ) : filteredLicenses.length === 0 ? (
              <div className="text-center p-6 bg-slate-50 rounded-md">
                <p className="text-muted-foreground">No licenses found. Try a different search term.</p>
              </div>
            ) : (
              <ScrollArea className="px-1 py-2" style={{height: "300px", maxHeight: "50vh"}}>
                <div className="space-y-2">
                  {filteredLicenses.map((license) => {
                    const isSelected = selectedLicenses.includes(license.id);
                    // Ensure we have valid numbers with fallbacks
                    const totalSeats = typeof license.totalSeats === 'number' ? license.totalSeats : 0;
                    const assignedSeats = typeof license.assignedSeats === 'number' ? license.assignedSeats : 0;
                    const availableSeats = Math.max(0, totalSeats - assignedSeats);
                    const hasAvailableSeats = availableSeats > 0;
                    // Get application name safely
                    const applicationName = license.application?.name || 'Unknown Application';
                    
                    return (
                      <div
                        key={license.id}
                        className={`flex items-center p-3 rounded-md ${
                          isSelected ? "bg-primary/5 border border-primary/20" : "hover:bg-muted"
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => hasAvailableSeats ? toggleLicense(license.id) : null}
                            id={`license-${license.id}`}
                            disabled={!hasAvailableSeats}
                          />
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-1">
                              <div className="flex items-center gap-2">
                                <label
                                  htmlFor={`license-${license.id}`}
                                  className="text-sm font-medium cursor-pointer"
                                >
                                  {license.name}
                                </label>
                                {license.type && (
                                  <span className="text-xs bg-slate-100 text-slate-700 rounded-full px-2 py-0.5">
                                    {license.type}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="flex justify-between items-center">
                              <div className="text-xs text-muted-foreground">
                                <span className="font-medium text-slate-700">
                                  {applicationName}
                                </span>
                                {license.expirationDate && ` • Expires: ${new Date(license.expirationDate).toLocaleDateString()}`}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs ${availableSeats > 0 ? 'bg-primary/10 text-primary' : 'bg-amber-100 text-amber-700'} rounded-full px-2 py-0.5 font-medium`}>
                                  {availableSeats === 0 ? 'No seats available' : `${availableSeats}/${totalSeats} seats available`}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
            
            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between items-center">
                <div className="text-sm text-muted-foreground">
                  {selectedLicenses.length > 0 ? (
                    <span>{selectedLicenses.length} licenses selected</span>
                  ) : (
                    <span>No licenses selected</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={handleClose}
                    disabled={assignLicensesToTeamMutation.isPending}
                  >
                    Skip for Now
                  </Button>
                  <LoadingButton
                    onClick={completeTeamSetup}
                    isLoading={assignLicensesToTeamMutation.isPending}
                  >
                    <Key className="h-4 w-4 mr-2" />
                    Assign Licenses
                  </LoadingButton>
                </div>
              </div>
            </div>
          </>
        )}
        
        {currentStep === "success" && (
          // Step 4: Success screen
          <>
            <DialogHeader>
              <DialogTitle>Team Setup Complete</DialogTitle>
              <DialogDescription>
                Your team has been successfully created with members and licenses assigned.
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex flex-col items-center justify-center py-8">
              <div className="bg-green-50 text-green-600 rounded-full p-4 mb-4">
                <Check className="h-12 w-12" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{createdTeamName}</h3>
              <div className="flex gap-6 mt-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{selectedEmployees.length}</div>
                  <div className="text-sm text-muted-foreground">Team Members</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{selectedLicenses.length}</div>
                  <div className="text-sm text-muted-foreground">Assigned Licenses</div>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button onClick={handleClose} className="w-full">
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}